import { ref } from "vue";
import axios from "axios";
import { logger } from "@/infrastructure/services/LoggerService";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { resolveFieldLabel } from "@/shared/i18n/filterFieldLabels";
import { FILTER_FIELD_TYPE_ICON, } from "@/shared/types/filterCatalog";
/**
 * OPHUB's `FeatureFlagFilter` errorCode when the `opqry-engine` feature flag is off
 * (the default when the config key is missing). Returned as HTTP 503.
 */
const OPQRY_ENGINE_DISABLED_ERROR_CODE = "Ophub.Feature.Disabled";
/**
 * Distinguishes "the opqry-engine flag is intentionally off" from a genuine failure.
 * The flag-off signal is a supported deployment state, not an error: OPHUB's
 * `FeatureFlagFilter` returns 503 with this errorCode, which must degrade quietly to
 * the no-fields empty state rather than surface a toast (see OP-6491 deployment note).
 */
function isFeatureDisabledResponse(e) {
    const status = axios.isAxiosError(e) ? e.response?.status : undefined;
    return status === 503 || getApiErrorCode(e) === OPQRY_ENGINE_DISABLED_ERROR_CODE;
}
/**
 * The Step 2 editor for one OPQRY field, or null when the panel has none for it: a field without options per
 * `FilterCatalogEditors.listItems`, or a dotted collection child path (`communicationChannels.value`), which the
 * quick filters do not offer (engine semantics: `ExpressionBuilder.BuildCollectionChildScalar`).
 */
function toEditor(field, e) {
    if (field.path.includes("."))
        return null;
    switch (field.type) {
        case "String":
            return { type: "text", placeholder: e.textPlaceholder };
        case "Enum":
        case "Guid": {
            const items = e.listItems[field.path];
            if (items?.length) {
                return {
                    type: "list",
                    items,
                    selectAllLabel: e.selectAllLabel,
                    step2SearchPlaceholder: e.step2SearchPlaceholder,
                };
            }
            return field.type === "Enum" && !items ? { type: "text", placeholder: e.textPlaceholder } : null;
        }
        case "Bool":
            return {
                type: "list",
                single: true,
                items: e.booleanItems,
                step2SearchPlaceholder: e.step2SearchPlaceholder,
            };
        case "Number":
        case "Decimal": {
            const bounds = e.rangeBounds?.[field.path];
            return {
                type: "range",
                rangeSeparatorLabel: e.rangeSeparatorLabel,
                rangeErrorLabel: e.rangeErrorLabel,
                rangeMinLabel: e.rangeMinLabel,
                rangeMaxLabel: e.rangeMaxLabel,
                rangeClampInputToBounds: bounds !== undefined,
                ...(bounds && { rangeMin: bounds.min, rangeMax: bounds.max, rangeStep: bounds.step }),
            };
        }
        case "DateTime":
            return {
                type: "dateRange",
                dateFormat: e.dateFormat,
                dateLanguage: e.dateLanguage,
                dateFromLabel: e.dateFromLabel,
                dateToLabel: e.dateToLabel,
            };
        default:
            return null;
    }
}
/**
 * Loads the OPQRY filter-metadata catalog `fetchCatalog` returns and maps it to the OPAL
 * `TableFilterSelector` field/group shape.
 *
 * Mapping rules: the backend's `group` (data origin, not path shape) decides root vs.
 * related — an `Own` field goes to the "root" group and an `AggregateSnapshot` one to "related":
 * a donor aggregate off `c.Donor!` has no dot in its path but still comes from another service's
 * table. `RelatedDetail` fields (third-party detail rows) are excluded entirely — this
 * panel has no saved-filter concern, unlike `useAdvancedFilter`. Each field gets the value
 * editor of its type from `editors` (see `toEditor`); a field with no editor is not offered. `editors` may be async (options
 * loaded from an API): it runs alongside `fetchCatalog`, and no field is offered before both settle.
 */
export function useFilterCatalog(fetchCatalog, labels, editors) {
    const fields = ref([]);
    const fieldGroups = ref([]);
    const descriptors = ref([]);
    const isLoading = ref(false);
    const hasError = ref(false);
    async function load() {
        isLoading.value = true;
        hasError.value = false;
        try {
            const [metadata, resolvedEditors] = await Promise.all([fetchCatalog(), editors()]);
            // Third-party detail rows are not offered in the filter panel (see the
            // advanced-filter design, "Hiding RelatedDetail without breaking saved filters").
            const offered = metadata.fields.filter((field) => field.group !== "RelatedDetail");
            const mapped = offered
                .map((field) => ({
                field,
                editor: toEditor(field, resolvedEditors),
            }))
                .filter((entry) => entry.editor !== null);
            fields.value = mapped.map(({ field, editor }) => ({
                key: field.path,
                label: resolveFieldLabel(field, labels.t),
                group: (field.group === "AggregateSnapshot"
                    ? "related"
                    : "root"),
                icon: FILTER_FIELD_TYPE_ICON[field.type],
                ...editor,
            }));
            descriptors.value = mapped.map(({ field }) => ({ path: field.path, type: field.type }));
            const rootCount = fields.value.filter((f) => f.group === "root").length;
            const relatedCount = fields.value.length - rootCount;
            fieldGroups.value = [
                { value: "root", label: `${labels.rootGroupLabel()} (${rootCount})` },
                { value: "related", label: `${labels.relatedGroupLabel()} (${relatedCount})` },
            ];
        }
        catch (e) {
            fields.value = [];
            descriptors.value = [];
            fieldGroups.value = [];
            if (isFeatureDisabledResponse(e)) {
                // Expected/supported state (opqry-engine flag off) — empty catalog, no toast.
                hasError.value = false;
                logger.debug("useFilterCatalog: opqry-engine feature flag is disabled — degrading to empty catalog", e);
            }
            else {
                hasError.value = true;
                logger.error("useFilterCatalog: failed to load filter metadata", e);
            }
        }
        finally {
            isLoading.value = false;
        }
    }
    return { fields, descriptors, fieldGroups, isLoading, hasError, load };
}
